import { clsx } from 'clsx';
import { FC, ReactNode } from 'react';

import { styled } from '@mui/material/styles';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';
import Divider, { dividerClasses } from '@o2/ui/Divider';
import Stack, { StackProps } from '@o2/ui/Stack';

import colors from '@o2/ui/theme/colors';

import fieldListClasses, { useFieldListUtilityClasses } from './fieldListClasses';
import FieldListItem from './Item/Item';

export interface FieldListProps extends Omit<StackProps, 'divider'> {
  /**
   * FieldList.Row array
   */
  children: ReactNode;

  /**
   * FieldList variant
   */
  variant?: 'default' | 'condensed';

  /**
   * Show divider between items.
   *
   * If the divider doesn't show, makqe sure that you are actually passing multiple children and not just a single JSX fragment!
   */
  divider?: boolean;

  /**
   * Disable horizontal spacing in Items
   */
  disableHorizontalSpacing?: boolean;
}

interface FieldList extends FC<FieldListProps> {
  Item: typeof FieldListItem;
}

const StyledStack = styled(Stack, {
  name: 'FieldList',
  slot: 'root',
  shouldForwardProp: (propName) =>
    sysShouldForwardProp(propName) && propName !== 'variant' && propName !== 'disableHorizontalSpacing',
})<{ variant: 'default' | 'condensed'; disableHorizontalSpacing?: boolean }>(
  ({ theme, variant, disableHorizontalSpacing }) => ({
    [`& .${fieldListClasses.label}, .${fieldListClasses.value}`]: {
      padding: theme.spacing(variant === 'condensed' ? 0.5 : 1, disableHorizontalSpacing ? 0 : 1),
    },
    [`& .${dividerClasses.root}`]: {
      borderColor: colors.transparentGray[200],
    },
    [`& .${fieldListClasses.vertical}`]: {
      [`& .${fieldListClasses.label}`]: {
        padding: theme.spacing(variant === 'condensed' ? 0.5 : 1, disableHorizontalSpacing ? 0 : 1, 0.25),
      },
      [`& .${fieldListClasses.value}`]: {
        padding: theme.spacing(0.25, disableHorizontalSpacing ? 0 : 1, variant === 'condensed' ? 0.5 : 1),
      },
    },
  })
);

const FieldList: FieldList = ({
  children,
  classes,
  variant = 'default',
  divider,
  disableHorizontalSpacing,
  ...other
}) => {
  const utilityClasses = useFieldListUtilityClasses(classes);

  return (
    <StyledStack
      className={clsx(utilityClasses.root)}
      disableHorizontalSpacing={disableHorizontalSpacing}
      variant={variant}
      direction="column"
      divider={divider ? <Divider /> : undefined}
      {...other}
    >
      {children}
    </StyledStack>
  );
};

FieldList.Item = FieldListItem;

export default FieldList;
